import React from 'react';
import { Layout } from '../components/Layout';
import { Link } from 'react-router-dom';
import { Server, Shield, Zap, Globe, Lock, Cpu, ArrowRight, Cloud, Check, MapPin, Activity, UserCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import Tuotteet from '../components/Tuotteet.tsx';

// Import generated images
import heroImage from '../assets/images/hero-helsinki-final.png';
import serverImage from '../assets/aigc/images/server-room_1768402418_000.png';
import natureImage from '../assets/aigc/images/nature-tech_1768402418_000.png';

export const Home: React.FC = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="Aurora Borealis over Finnish City"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-slate-900/90" />
        </div>

        <div className="container mx-auto px-6 relative z-10 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span
              className="inline-block py-1 px-3 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-sm font-medium mb-6 backdrop-blur-sm"
              style={{
                fontStyle: "normal",
                textDecoration: "none"
              }}>Kotimainen, kyberturvallinen ja suvereeni infra</span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-tight">Turvalliset konesali- ja pilvipalvelut<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Helsingin Pitäjänmäellä</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-10">Helsinki DC tarjoaa vakaita, turvallisia ja nykyaikaisia AI-aikakauden konesali- ja tietoliikennepalveluita Pitäjänmäellä Helsingissä – ihanteellisella sijainnilla palvelimien sijoittamiseen ja infrastruktuurin hajauttamiseen.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/services" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-semibold transition-all flex items-center justify-center gap-2">Tutustu palveluihin<ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/10 text-white px-8 py-4 rounded-full font-semibold transition-all">Ota yhteyttä</Link>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Features Grid */}
      <section id="services" className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Etsitkö nykyaikaista sijoituspaikkaa palvelimillesi ja tietoliikennelaitteillesi?</h2>
            <p className="text-slate-600 text-lg">Helsinki DC tarjoaa monipuoliset ja nopeat yhteydet kaikkialle maailmaan, mukaan lukien Venäjälle, Aasiaan ja Baltiaan. Vikasietoinen runkoverkkomme sisältää useita itsenäisiä reittejä, varmistaen katkeamattoman ja nopean tiedonsiirron. Tarjoamme myös tehokkaan DDoS-suojan, joka suodattaa haitallisen liikenteen automaattisesti varmistaen palveluiden katkeamattoman toiminnan.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Server className="w-8 h-8 text-blue-600" />,
                title: "Colocation",
                desc: "Tuo omat palvelimet palvelinsaliin, joka on toimintavarma ja energiatehokas. Helsinki DC täyttää turvallisuus-, jäähdytys-, energiatehokkuus- ja sähköstandardit."
              },
              {
                icon: <Cloud className="w-8 h-8 text-emerald-600" />,
                title: "Cloud",
                desc: "Joustavat pilvipalvelut laskenta- ja tallennusoptimoidut pilvipalvelimet. KVM, VMware ja OpenStack."
              },
              {
                icon: <Shield className="w-8 h-8 text-indigo-600" />,
                title: "Private Cloud",
                desc: "Datasi pysyy Suomessa. GDPR-vaatimukset täyttävä fyysinen ja digitaalinen ratkaisu."
              },
              {
                icon: <Cpu className="w-8 h-8 text-orange-600" />,
                title: "Baremetal",
                desc: "Jätä raudan hankinta ja ylläpito huoleksemme. Tehokkaat dedikoidut palvelimet täydellä pääkäyttäjän oikeuksilla. Ihanteellinen intensiivisille työkuormille ja mukautetulle virtualisoinnille. Nopeat 10 Gbit/s yhteydet ja NVMe levyjärjestelmät."
              },
              {
                icon: <Globe className="w-8 h-8 text-cyan-600" />,
                title: "Verkkoyhteydet",
                desc: "Verkkoyhteydet saatavilla kaikilta operaattoreilta, DWDM-yhteydet FICIX-solmupisteisiin ja DDoS-suojattu verkkoyhteys jopa 100 Gbit/s nopeudella."
              },
              {
                icon: <Lock className="w-8 h-8 text-red-600" />,
                title: "Kyberturvallisuus",
                desc: "Edistynyt uhkien torjunta, palvelunestohyökkäysten torjunta ja 24/7-tietoturvan valvonta infrastruktuurisi turvallisuuden takaamiseksi."
              }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-slate-100"
              >
                <div className="bg-slate-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Nature Tech Section */}
      <section id="about" className="py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img src={natureImage} alt="Nature and Technology" className="w-full" />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-transparent mix-blend-overlay" />
              </div>
            </div>
            <div className="lg:w-1/2">
              <div className="flex items-center gap-2 text-emerald-600 font-semibold mb-4">
                <Zap className="w-5 h-5" />
                <span>Vihreän energian konesali -  puhtaasti Suomessa</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Optimoitu hiilijalanjälki
              </h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed">
                Suomi on Euroopan kärkijoukkue vihreässä datataloudessa - optimaalinen ilmasto, tuulivoima, vahva energiavarasto ja vakaa sähköverkko takaavat luotettavuuden myös tekoälyn ja suurten laskentamäärien aikakaudella.
              </p>
              <ul className="space-y-4">
                {[
                  "Käytämme uusiutuvaa sähköenergiaa",
                  "Energiatehokas jäähdytys",
                  "PUE < 1.15 Efficiency Rating",
                  "Hukkalämpö hyötykäytössä"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      {/* Tech Specs Section */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={serverImage} alt="Server Room" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-slate-900/90" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { icon: <Lock />, label: "Vartiointi, kameravalvonta ja ympäristön parametrien monitorointi", value: "24/7" },
              { icon: <Globe />, label: "Nopeat yhteydet suomalaisiin ja kansainvälisiin verkkoihin. Yhdistämispalvelut solmupisteisiin (Ficix) ja Eurooppaan (C-Lion 1).", value: "100 Gbps+" },
              { icon: <Zap />, label: "Kahdennettu tekninen ratkaisu mahdollistaa palveluiden keskeytyksettömän toiminnan.", value: "99.999%" },
              { icon: <Cpu />, label: "AI-laskentatehoa jopa 20 kW/rack-kaappi", value: "20 KW" },
            ].map((stat, idx) => (
              <div key={idx} className="p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
                <div className="flex justify-center mb-4 text-blue-400">
                  {React.cloneElement(stat.icon as React.ReactElement, { className: "w-8 h-8" })}
                </div>
                <div className="text-3xl font-bold mb-2">{stat.value}</div>
                <div className="text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Five Reasons Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Viisi syytä valita Helsinki DC</h2>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
            {[
              { icon: <MapPin className="w-8 h-8 text-blue-600" />, title: "Sijainti", desc: "Turvallinen ja keskeinen sijainti Helsingissä" },
              { icon: <Globe className="w-8 h-8 text-blue-600" />, title: "Yhteydet", desc: "Huippuluokan tietoliikenneyhteydet maailmalle ja Suomeen" },
              { icon: <Zap className="w-8 h-8 text-blue-600" />, title: "Tehokkuus", desc: "Energiatehokas ja innovatiivinen suunnittelu tuottaa kustannustehokkuutta asiakkaillemme" },
              { icon: <Activity className="w-8 h-8 text-blue-600" />, title: "Käytettävyys", desc: "Kahdennettu kokonaisratkais varmistaa palveluiden toimintavarmuuden" },
              { icon: <UserCheck className="w-8 h-8 text-blue-600" />, title: "Palvelu", desc: "Ammattitaitoiset ja ystävälliset asiantuntijat toteuttavat remotehands- ja teknisiä pyyntöjä" }
            ].map((reason, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-slate-100 text-center">
                <div className="flex justify-center mb-4">
                  {reason.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{reason.title}</h3>
                <p className="text-slate-600">{reason.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

            <Tuotteet/>

      {/* CTA Section */}
      <section id="contact" className="py-24 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Haluatko kuulla lisää?</h2>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-10">
            Liity tyytyväisten asiakkaittemme joukkoon.
            Turvallisuus, suvereniteetti ja kestävyys yhdessä. Suomalaiset yritykset luottavat meihin
            kriittisen infrastruktuurinsa kodiksi.
          </p>
          <Link to="/contact" className="bg-white text-blue-600 px-8 py-4 rounded-full font-bold hover:bg-blue-50 transition-colors shadow-lg">
            Tilaa tutustumiskäynti palvelinsaliin
          </Link>
        </div>
      </section>
    </Layout>
  );
};
